<template>
  <div>
    <Nav></Nav>
    <b-container fluid="xl">
      <b-row class="mt-3 align-items-center">
        <b-col>
          <h1 class="mb-0">Promociones</h1>
          <p class="text-muted mb-0">
            Crea promociones para lo que ve el cliente en la app propia y el BFF. Usan el motor de
            beneficios: se guardan como definiciones y se aplican automáticamente al pedido.
          </p>
        </b-col>
        <b-col cols="auto">
          <b-button variant="primary" @click="openPicker">+ Nueva promoción</b-button>
        </b-col>
      </b-row>

      <!-- Lista de promociones -->
      <b-card class="mt-3">
        <b-table
          striped hover responsive small show-empty
          :busy="loading"
          :items="promos"
          :fields="fields"
          empty-text="Aún no hay promociones. Crea la primera con «Nueva promoción»."
        >
          <template #table-busy>
            <div class="text-center text-primary my-3"><b-spinner></b-spinner></div>
          </template>
          <template #cell(type)="d">
            <b-badge variant="info">{{ typeLabel(d.item) }}</b-badge>
          </template>
          <template #cell(value)="d">{{ valueLabel(d.item) }}</template>
          <template #cell(min_subtotal)="d">{{ d.item.min_subtotal ? money(d.item.min_subtotal) : '—' }}</template>
          <template #cell(vigencia)="d">{{ vigencia(d.item) }}</template>
          <template #cell(active)="d">
            <b-badge :variant="d.item.active ? 'success' : 'secondary'">{{ d.item.active ? 'Activa' : 'Inactiva' }}</b-badge>
            <b-badge v-if="d.item.show_in_app === false" variant="dark" class="ml-1">Oculta en app</b-badge>
          </template>
          <template #cell(actions)="d">
            <b-button size="sm" variant="outline-primary" class="mr-1" @click="edit(d.item)">Editar</b-button>
            <b-button size="sm" variant="outline-danger" @click="remove(d.item)">Eliminar</b-button>
          </template>
        </b-table>
      </b-card>
    </b-container>

    <!-- Paso 1: seleccionar tipo -->
    <b-modal v-model="showPicker" title="Selecciona el tipo de promoción" size="lg" hide-footer>
      <b-row>
        <b-col md="6" v-for="t in types" :key="t.key" class="mb-3">
          <b-card class="h-100 type-card" body-class="py-3" @click="pickType(t)">
            <div class="font-weight-bold">{{ t.title }}</div>
            <div class="small text-muted">{{ t.desc }}</div>
          </b-card>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Paso 2: formulario -->
    <b-modal
      v-model="showForm"
      :title="(form.id ? 'Editar' : 'Nueva') + ' promoción — ' + (selectedType ? selectedType.title : '')"
      size="lg"
      ok-title="Guardar"
      cancel-title="Cancelar"
      @ok.prevent="save"
    >
      <b-form-group label="Nombre de la promoción" description="Lo que identifica la promo internamente.">
        <b-form-input v-model="form.name" placeholder="Ej. 20% de ahorro en pedidos de $250+"></b-form-input>
      </b-form-group>

      <!-- Valor según el tipo -->
      <b-form-group v-if="selectedType && selectedType.valueKind === 'percent'" label="% de descuento">
        <div class="mb-1">
          <b-button v-for="p in [10,15,20,25,30]" :key="p" size="sm" class="mr-1"
            :variant="form.effect_value === p ? 'primary' : 'outline-secondary'" @click="form.effect_value = p">{{ p }}%</b-button>
        </div>
        <b-form-input type="number" step="1" min="0" max="100" v-model.number="form.effect_value"></b-form-input>
      </b-form-group>

      <b-form-group v-if="selectedType && selectedType.valueKind === 'money'"
        :label="selectedType.valueLabel">
        <b-form-input type="number" step="0.01" min="0" v-model.number="form.effect_value"></b-form-input>
      </b-form-group>

      <!-- Gasto mínimo -->
      <b-form-group v-if="selectedType && selectedType.showMin"
        label="Gasto mínimo" :description="minRequired ? 'Requerido para este tipo.' : 'Opcional.'">
        <div class="mb-1">
          <b-button v-for="m in [150,250,350,500]" :key="m" size="sm" class="mr-1"
            :variant="form.min_subtotal === m ? 'primary' : 'outline-secondary'" @click="form.min_subtotal = m">${{ m }}</b-button>
        </div>
        <b-form-input type="number" step="0.01" min="0" v-model.number="form.min_subtotal"></b-form-input>
      </b-form-group>

      <!-- Productos objetivo -->
      <template v-if="selectedType && selectedType.productMode === 'multi'">
        <b-form-group label="Productos" description="Una sola promoción aplica a todos los productos elegidos.">
          <vu-select
            v-model="ui.productIds"
            :options="productPickOptions"
            :reduce="o => o.value"
            label="text"
            multiple
            :close-on-select="false"
            placeholder="Busca y agrega productos..."
          ></vu-select>
          <div class="small text-muted mt-1">{{ ui.productIds.length }} producto(s) seleccionado(s)</div>
        </b-form-group>
      </template>
      <template v-else-if="selectedType && selectedType.productMode === 'single'">
        <b-form-group label="Producto de regalo">
          <b-form-select v-model="form.effect_product_id" :options="productOptions"></b-form-select>
        </b-form-group>
      </template>

      <!-- Canales / modalidades -->
      <b-form-group label="Canales" description="Dónde se muestra/aplica la promo.">
        <b-form-checkbox-group v-model="ui.channels" :options="channelOptions"></b-form-checkbox-group>
      </b-form-group>
      <b-form-group label="Modalidades (opcional)">
        <b-form-checkbox-group v-model="ui.fulfillments" :options="fulfillmentOptions"></b-form-checkbox-group>
      </b-form-group>
      <b-form-group label="Días de la semana (opcional)">
        <b-form-checkbox-group v-model="ui.weekdays" :options="weekdayOptions"></b-form-checkbox-group>
      </b-form-group>

      <!-- Segmentación por sucursal -->
      <b-form-group label="Sucursales" description="Deja vacío para todas. Selecciona para limitar la promo a sucursales específicas.">
        <vu-select
          v-model="ui.sucursalIds"
          :options="sucursalPickOptions"
          :reduce="o => o.value"
          label="text"
          multiple
          :close-on-select="false"
          placeholder="Todas las sucursales (busca para limitar)"
        ></vu-select>
        <div class="small text-muted mt-1">
          {{ ui.sucursalIds.length ? ui.sucursalIds.length + ' sucursal(es) seleccionada(s)' : 'Todas las sucursales' }}
        </div>
      </b-form-group>

      <!-- Vigencia -->
      <b-row>
        <b-col md="6"><b-form-group label="Desde"><b-form-input type="date" v-model="form.starts_at"></b-form-input></b-form-group></b-col>
        <b-col md="6"><b-form-group label="Hasta"><b-form-input type="date" v-model="form.ends_at"></b-form-input></b-form-group></b-col>
      </b-row>

      <!-- Configuración avanzada -->
      <b-button v-b-toggle.promo-adv variant="link" class="px-0">Configuración avanzada</b-button>
      <b-collapse id="promo-adv">
        <b-row>
          <b-col md="6">
            <b-form-group label="Nivel de lealtad mínimo (opcional)" description="Deja «Todos» para no restringir por nivel.">
              <b-form-select v-model="form.requires_tier_id" :options="tierOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Producto de descuento (opcional)" description="Línea con la que se materializa; si se omite se reparte sobre los precios.">
              <b-form-select v-model="form.discount_product_id" :options="discountProductOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="selectedType && (selectedType.effect_type === 'bogo' || selectedType.effect_type === 'free_product')">
            <b-form-group label="Cantidad sin costo" description="Unidades gratis por aplicación.">
              <b-form-input type="number" min="1" v-model.number="form.effect_qty"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Código (opcional)" description="Se genera solo si lo dejas vacío.">
              <b-form-input v-model="form.code" placeholder="PROMO_..."></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-checkbox v-model="form.requires_code" switch>Cupón (requiere código para aplicarse)</b-form-checkbox>
        <b-form-checkbox v-model="form.auto_apply" switch>Aplicación automática (se aplica sin código al cumplir las condiciones)</b-form-checkbox>
        <b-form-checkbox v-model="form.stackable" switch>Acumulable con otras promociones</b-form-checkbox>
        <b-form-checkbox v-model="form.active" switch>Activa</b-form-checkbox>
        <b-form-checkbox v-model="form.show_in_app" switch>Mostrar en la app (Beneficios por nivel). Si se apaga, la promoción sigue aplicando pero no aparece en la lista.</b-form-checkbox>
      </b-collapse>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../../components/Nav.vue";
import benefitsService from "../../services/benefitsService";
import loyaltyService from "../../services/loyaltyService";

// Captura de promociones (propuesta integral — vitrina sobre el motor de beneficios,
// Fase 3). Cada tipo es un preset que arma un benefit_definitions con auto_apply=1.
// No agrega lógica nueva de backend: usa POST/PUT /benefit-definitions existente.
export default {
  name: "promociones",
  components: { Nav },
  data() {
    return {
      loading: false,
      promos: [],
      posProducts: [],
      tiers: [],
      sucursals: [],
      showPicker: false,
      showForm: false,
      selectedType: null,
      form: this.blankForm(),
      ui: { channels: [], fulfillments: [], weekdays: [], productIds: [], sucursalIds: [] },
      fields: [
        { key: "name", label: "Promoción" },
        { key: "type", label: "Tipo" },
        { key: "value", label: "Valor" },
        { key: "min_subtotal", label: "Mínimo" },
        { key: "vigencia", label: "Vigencia" },
        { key: "active", label: "Estado" },
        { key: "actions", label: "" },
      ],
      // Los 6 tipos cubiertos hoy por el motor (04 §2 R7/R15). Hora feliz y precio fijo
      // combo quedan fuera hasta agregar campos al motor.
      types: [
        { key: "order_percent", title: "Porcentaje de ahorro en el pedido", desc: "% de descuento a todo el pedido sobre un gasto mínimo.", effect_type: "percent_off", valueKind: "percent", showMin: true, productMode: "none" },
        { key: "order_amount", title: "Monto de ahorro del pedido", desc: "Descuento de monto fijo sobre un gasto mínimo.", effect_type: "amount_off", valueKind: "money", valueLabel: "Monto de descuento ($)", showMin: true, productMode: "none" },
        { key: "item_percent", title: "Porcentaje de ahorro en artículos", desc: "% de descuento a los productos seleccionados.", effect_type: "percent_off", valueKind: "percent", showMin: false, productMode: "multi" },
        { key: "bogo", title: "2x1 / Compra uno y llévate otro", desc: "Otra unidad de los productos seleccionados sin costo.", effect_type: "bogo", valueKind: "none", showMin: false, productMode: "multi" },
        { key: "free_item", title: "Artículo sin costo al comprar", desc: "Un producto de regalo al superar un gasto mínimo.", effect_type: "free_product", valueKind: "none", showMin: true, productMode: "single" },
        { key: "free_shipping", title: "Envío sin costo", desc: "Envío gratis (con gasto mínimo opcional).", effect_type: "free_shipping", valueKind: "money", valueLabel: "Tope de envío ($, opcional)", showMin: true, productMode: "none" },
      ],
      channelOptions: [
        { value: "app", text: "App propia" },
        { value: "pos", text: "Salón / POS" },
        { value: "uber", text: "Uber Eats" },
        { value: "rappi", text: "Rappi" },
        { value: "pidedirecto", text: "PideDirecto" },
        { value: "whatsapp", text: "WhatsApp" },
      ],
      fulfillmentOptions: [
        { value: "delivery", text: "Domicilio" },
        { value: "pickup", text: "Recoger" },
        { value: "takeaway", text: "Para llevar" },
        { value: "dinein", text: "En sitio" },
      ],
      weekdayOptions: [
        { value: 1, text: "Lun" }, { value: 2, text: "Mar" }, { value: 3, text: "Mié" },
        { value: 4, text: "Jue" }, { value: 5, text: "Vie" }, { value: 6, text: "Sáb" }, { value: 7, text: "Dom" },
      ],
    };
  },
  computed: {
    minRequired() {
      return this.selectedType && ["order_percent", "order_amount", "free_item"].includes(this.selectedType.key);
    },
    productOptions() {
      return [{ value: null, text: "— selecciona —" }].concat(
        this.posProducts.map((p) => ({ value: p.id, text: (p.clave ? p.clave + " · " : "") + (p.short_name || p.ndescription || "#" + p.id) }))
      );
    },
    productPickOptions() {
      return this.posProducts.map((p) => ({ value: p.id, text: (p.clave ? p.clave + " · " : "") + (p.short_name || p.ndescription || "#" + p.id) }));
    },
    sucursalPickOptions() {
      return this.sucursals.map((s) => ({ value: s.id, text: s.name || s.nombre || ("Sucursal " + s.id) }));
    },
    discountProductOptions() {
      return [{ value: null, text: "— sin producto (se reparte sobre los precios) —" }].concat(
        this.posProducts.map((p) => ({ value: p.id, text: (p.clave ? p.clave + " · " : "") + (p.short_name || p.ndescription || "#" + p.id) }))
      );
    },
    tierOptions() {
      const sorted = [...this.tiers].sort((a, b) => (a.rank || 0) - (b.rank || 0));
      return [{ value: null, text: "Todos los niveles" }].concat(
        sorted.map((t) => ({ value: t.id, text: (t.name || t.code || ("#" + t.id)) + (t.code ? " (" + t.code + ")" : "") }))
      );
    },
  },
  mounted() {
    this.loadPromos();
    axios.get("/products").then((r) => { this.posProducts = r.data || []; }).catch(() => {});
    loyaltyService.getTiers().then((r) => { this.tiers = r.data || []; }).catch(() => {});
    axios.get("/sucursals").then((r) => { this.sucursals = r.data || []; }).catch(() => {});
  },
  methods: {
    blankForm() {
      return {
        id: 0, code: "", name: "", description: "", active: true, show_in_app: true, starts_at: "", ends_at: "",
        effect_type: "percent_off", effect_value: 0, effect_max_amount: null, effect_qty: null,
        effect_product_id: null, effect_product_group_id: null, effect_product_ids: null, applies_sucursales: null, discount_product_id: null, min_subtotal: null,
        requires_tier_id: null, tier_match: "min", max_tier_id: null,
        stackable: false, points_on_remainder: false, auto_apply: true, requires_code: false,
        generates_points: false, trigger_type: "manual", validity_mode: "days", validity_days: null,
        valid_until: "", max_uses_per_issue: 1,
      };
    },
    loadPromos() {
      this.loading = true;
      benefitsService.listDefinitions()
        .then((res) => {
          // La tabla benefit_definitions es compartida con los beneficios de ciclo de
          // vida (bienvenida, cumpleaños, etc.). Aquí mostramos solo promociones: las
          // de disparador manual (creadas en esta pantalla). Las demás se gestionan en
          // Lealtad → Beneficios.
          this.promos = (res.data || []).filter((d) => {
            const t = this.strOf(d.trigger_type);
            return t === "" || t === "manual";
          });
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => { this.loading = false; });
    },
    openPicker() {
      this.showPicker = true;
    },
    pickType(t) {
      this.selectedType = t;
      this.form = this.blankForm();
      this.form.effect_type = t.effect_type;
      // Defaults sensatos por tipo.
      if (t.valueKind === "percent") this.form.effect_value = 20;
      else if (t.key === "free_shipping") this.form.effect_value = 999; // cubre todo el envío (motor aplica min(costo, valor))
      else this.form.effect_value = 0;
      this.ui = { channels: ["app"], fulfillments: [], weekdays: [], productIds: [], sucursalIds: [] };
      this.showPicker = false;
      this.showForm = true;
    },
    edit(def) {
      this.selectedType = this.detectType(def);
      this.form = { ...this.blankForm(), ...def };
      this.ui = {
        channels: this.parseArr(def.applies_channels),
        fulfillments: this.parseArr(def.applies_fulfillments),
        weekdays: this.parseArr(def.weekdays),
        productIds: this.parseArr(def.effect_product_ids).map((n) => Number(n)),
        sucursalIds: this.parseArr(def.applies_sucursales).map((n) => Number(n)),
      };
      this.form.starts_at = this.dateOnly(def.starts_at);
      this.form.ends_at = this.dateOnly(def.ends_at);
      this.showForm = true;
    },
    detectType(def) {
      const et = this.strOf(def.effect_type);
      const hasList = this.parseArr(def.effect_product_ids).length > 0;
      const hasProd = def.effect_product_id || def.effect_product_group_id || hasList;
      if (et === "amount_off") return this.types.find((t) => t.key === "order_amount");
      if (et === "bogo") return this.types.find((t) => t.key === "bogo");
      if (et === "free_product") return this.types.find((t) => t.key === "free_item");
      if (et === "free_shipping") return this.types.find((t) => t.key === "free_shipping");
      if (et === "percent_off") return this.types.find((t) => t.key === (hasProd ? "item_percent" : "order_percent"));
      return this.types[0];
    },
    save() {
      if (!this.form.name) { this.makeToast("Falta el nombre", "Ponle un nombre a la promoción.", "warning"); return; }
      if (this.minRequired && !this.form.min_subtotal) { this.makeToast("Falta el mínimo", "Este tipo requiere un gasto mínimo.", "warning"); return; }
      if (this.selectedType.productMode === "multi" && !this.ui.productIds.length) { this.makeToast("Faltan productos", "Selecciona al menos un producto.", "warning"); return; }
      if (this.selectedType.productMode === "single" && !this.form.effect_product_id) { this.makeToast("Falta el producto", "Selecciona el producto de regalo.", "warning"); return; }

      const payload = this.buildPayload();
      const req = this.form.id ? benefitsService.updateDefinition(this.form.id, payload) : benefitsService.createDefinition(payload);
      req.then(() => {
        this.makeToast("Guardada", "Promoción guardada.", "success");
        this.showForm = false;
        this.loadPromos();
      }).catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
    },
    buildPayload() {
      const p = { ...this.form };
      const mode = this.selectedType ? this.selectedType.productMode : "none";
      // Nunca usamos grupo en esta pantalla.
      p.effect_product_group_id = null;
      if (mode === "multi") {
        // Una promo, N productos: lista de ids en effect_product_ids.
        p.effect_product_ids = this.ui.productIds.length ? JSON.stringify(this.ui.productIds.map((n) => Number(n))) : null;
        p.effect_product_id = null;
      } else if (mode === "single") {
        // Producto de regalo: un solo effect_product_id.
        p.effect_product_ids = null;
      } else {
        p.effect_product_ids = null;
        p.effect_product_id = null;
      }
      // Código autogenerado si no se dio.
      if (!p.code) p.code = this.genCode(p.name);
      // Arrays → strings JSON (contrato del backend, igual que Beneficio.vue).
      p.applies_channels = this.arrToJson(this.ui.channels, false);
      p.applies_fulfillments = this.arrToJson(this.ui.fulfillments, false);
      p.weekdays = this.arrToJson(this.ui.weekdays, true);
      p.applies_sucursales = this.arrToJson(this.ui.sucursalIds, true);
      p.excluded_product_flags = null;
      p.trigger_config = null;
      return p;
    },
    remove(def) {
      this.$bvModal.msgBoxConfirm(`¿Eliminar la promoción «${def.name}»?`, { okVariant: "danger", okTitle: "Eliminar", cancelTitle: "Cancelar" })
        .then((ok) => {
          if (!ok) return;
          benefitsService.deleteDefinition(def.id)
            .then(() => { this.makeToast("Eliminada", "Promoción eliminada.", "success"); this.loadPromos(); })
            .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
        });
    },
    genCode(name) {
      const slug = (name || "PROMO").toUpperCase().normalize("NFD").replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 20) || "PROMO";
      return slug + "_" + Date.now().toString(36).slice(-4).toUpperCase();
    },
    arrToJson(arr, numeric) {
      if (!arr || !arr.length) return null;
      return JSON.stringify(numeric ? arr.map((x) => parseInt(x, 10)) : arr);
    },
    parseArr(v) {
      const s = this.strOf(v);
      if (!s) return [];
      try { const a = JSON.parse(s); return Array.isArray(a) ? a : []; } catch (e) { return []; }
    },
    strOf(v) {
      if (v === null || v === undefined) return "";
      if (typeof v === "string") return v;
      if (v.String !== undefined) return v.Valid ? v.String : "";
      return JSON.stringify(v);
    },
    dateOnly(v) {
      const s = this.strOf(v);
      return s ? s.slice(0, 10) : "";
    },
    // Etiquetas para la tabla.
    typeLabel(def) {
      const t = this.detectType(def);
      return t ? t.title : this.strOf(def.effect_type);
    },
    valueLabel(def) {
      const et = this.strOf(def.effect_type);
      if (et === "percent_off") return (def.effect_value || 0) + "%";
      if (et === "amount_off" || et === "credit") return this.money(def.effect_value);
      if (et === "free_shipping") return "Envío gratis";
      if (et === "bogo") return "2x1";
      if (et === "free_product") return "Producto gratis";
      return "—";
    },
    vigencia(def) {
      const a = this.dateOnly(def.starts_at), b = this.dateOnly(def.ends_at);
      if (!a && !b) return "Sin límite";
      return (a || "…") + " → " + (b || "…");
    },
    money(v) {
      const n = Number(v || 0);
      return "$" + n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    errMsg(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string" ? err.response.data : (err.response.data.error || JSON.stringify(err.response.data));
      }
      return err && err.message ? err.message : "Error desconocido";
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    },
  },
};
</script>

<style scoped>
.type-card { cursor: pointer; transition: box-shadow .15s; }
.type-card:hover { box-shadow: 0 0 0 2px rgba(94, 114, 228, .35); }
</style>
